/* eslint-disable */
/**
 * PatientStudiesTab - Studies tab in patient detail (IDE/Doctor view)
 * Shows: active enrollments, pending questionnaires, response history with scores
 */

import { useState, useEffect, useCallback } from "react";
import api from "@/api";
import { toast } from "sonner";
import {
  FlaskConical, ClipboardList, Calendar, CheckCircle,
  Clock, ChevronRight, UserPlus, X, AlertTriangle,
  BarChart3, Target
} from "lucide-react";

function getVisitQuestionnaireStatus(visit) {
  const today = new Date().toISOString().split("T")[0];
  const questionnaires = visit.questionnaires || [];
  if (!questionnaires.length) return { tone: "red", label: "Aucun questionnaire assigné" };
  if (visit.completed) return { tone: "green", label: "Complété" };
  const due = visit.dueDate || today;
  const windowDays = visit.windowDays || 3;
  const dueDate = new Date(`${due}T00:00:00`);
  const todayDate = new Date(`${today}T00:00:00`);
  const daysDiff = Math.round((todayDate - dueDate) / 86400000);
  if (daysDiff > windowDays) return { tone: "amber", label: `Fenêtre dépassée (+${daysDiff}j)` };
  if (daysDiff >= -windowDays) return { tone: "blue", label: "Visible patient" };
  return { tone: "slate", label: "Programmé" };
}

const VISIT_STATUS_CLASSES = {
  red: "border-red-200 bg-red-50 text-red-700",
  amber: "border-amber-200 bg-amber-50 text-amber-700",
  blue: "border-blue-200 bg-blue-50 text-blue-700",
  green: "border-emerald-200 bg-emerald-50 text-emerald-700",
  slate: "border-slate-200 bg-slate-50 text-slate-600",
};

export default function PatientStudiesTab({ patientId }) {
  const [enrollments, setEnrollments] = useState([]);
  const [availableStudies, setAvailableStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEnroll, setShowEnroll] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [responses, setResponses] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Get all studies and check which ones this patient is enrolled in
      const studiesRes = await api.get("/studies?status=active");
      const studies = studiesRes.data;

      // Get enrollments for this patient across all studies
      const patientEnrollments = [];
      for (const study of studies) {
        try {
          const enrollRes = await api.get(`/studies/${study.id}/enrollments`);
          const patientEnroll = enrollRes.data.filter(e => e.patientId === patientId);
          for (const e of patientEnroll) {
            patientEnrollments.push({ ...e, studyName: study.name, studySponsor: study.sponsor, studyId: study.id });
          }
        } catch {
          // Skip if error
        }
      }
      setEnrollments(patientEnrollments);

      // Studies where patient is NOT enrolled
      const enrolledStudyIds = new Set(patientEnrollments.map(e => e.studyId || e.studyId));
      setAvailableStudies(studies.filter(s => !enrolledStudyIds.has(s.id)));
    } catch (err) {
      // error handled by toast
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleEnroll = async (studyId) => {
    try {
      await api.post(`/studies/${studyId}/enroll`, {
        patientId,
        consentDate: new Date().toISOString().split("T")[0],
        consentMethod: "electronic",
      });
      toast.success("Patient enrôlé dans l’étude");
      setShowEnroll(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l’enrôlement");
    }
  };

  const viewResponses = async (enrollment) => {
    try {
      const res = await api.get(`/studies/enrollments/${enrollment.id}/responses`);
      setResponses(res.data);
      setSelectedEnrollment(enrollment);
    } catch {
      toast.error("Erreur chargement des reponses");
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-400">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <FlaskConical className="w-5 h-5 text-indigo-600" />
          Etudes cliniques
        </h3>
        <button
          onClick={() => setShowEnroll(true)}
          data-testid="enroll-patient-study-btn"
          className="flex items-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-100 transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          Enroler dans une etude
        </button>
      </div>

      {/* Active Enrollments */}
      {enrollments.length === 0 ? (
        <div className="text-center py-10 bg-slate-50 rounded-xl">
          <FlaskConical className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">Ce patient n’est enrôlé dans aucune étude</p>
          <button
            onClick={() => setShowEnroll(true)}
            className="mt-3 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
          >
            Voir les etudes disponibles
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {enrollments.map(enrollment => {
            const totalVisits = enrollment.plannedVisits?.length || 0;
            const completedVisits = enrollment.completedVisits?.length || 0;
            const progress = totalVisits > 0 ? Math.round(completedVisits / totalVisits * 100) : 0;
            const nextVisit = enrollment.nextVisit;

            return (
              <div key={enrollment.id} className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-slate-800">{enrollment.studyName}</h4>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        enrollment.status === "active" ? "bg-emerald-50 text-emerald-700" :
                        enrollment.status === "completed" ? "bg-blue-50 text-blue-700" :
                        "bg-slate-100 text-slate-500"
                      }`}>
                        {enrollment.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500">{enrollment.studySponsor} - Consentement: {enrollment.consentDate}</p>
                  </div>
                  <button
                    onClick={() => viewResponses(enrollment)}
                    data-testid={`view-responses-${enrollment.id}`}
                    className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    <BarChart3 className="w-4 h-4" />
                    Voir les scores
                  </button>
                </div>

                {/* Progress bar */}
                <div>
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                    <span>Progression: {completedVisits}/{totalVisits} visites</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
                  </div>
                </div>

                {/* Next visit */}
                {nextVisit && (
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                    <Clock className="w-4 h-4 text-amber-600" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Prochaine visite: {nextVisit.name}</p>
                      <p className="text-xs text-amber-600">Date prevue: {nextVisit.dueDate} (fenetre +/- {nextVisit.windowDays || 3}j)</p>
                    {!nextVisit.questionnaires?.length && (
                      <p className="mt-1 text-xs font-semibold text-red-700" data-testid={`next-visit-missing-questionnaires-${enrollment.id}`}>
                        Aucun questionnaire assigné : le patient ne verra rien.
                      </p>
                    )}
                    </div>
                    {nextVisit.questionnaires?.length > 0 && (
                      <div className="ml-auto flex gap-1">
                        {nextVisit.questionnaires.map(q => (
                          <span key={q} className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">{q.replace(/_/g, " ")}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Visit timeline */}
                <div className="flex items-center gap-2 overflow-x-auto py-1">
                  {(enrollment.plannedVisits || []).map((visit, idx) => {
                    const status = getVisitQuestionnaireStatus(visit);
                    return (
                      <div
                        key={idx}
                        className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium ${VISIT_STATUS_CLASSES[status.tone]}`}
                        data-testid={`visit-questionnaire-status-${enrollment.id}-${idx}`}
                        title={status.label}
                      >
                        {visit.completed ? <CheckCircle className="w-3 h-3" /> : <Calendar className="w-3 h-3" />}
                        {visit.name} · {status.label}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Enroll Modal */}
      {showEnroll && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[70vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="font-bold text-slate-800">Enroler dans une etude</h3>
              <button onClick={() => setShowEnroll(false)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {availableStudies.length === 0 ? (
                <div className="text-center py-6">
                  <FlaskConical className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-500 text-sm font-medium">
                    {enrollments.length > 0
                      ? "Ce patient est deja enrole dans toutes les etudes actives"
                      : "Aucune etude active disponible"}
                  </p>
                  <p className="text-slate-400 text-xs mt-1">
                    Créez une nouvelle étude depuis l&rsquo;onglet DATA {">"} Études du Super Admin
                  </p>
                </div>
              ) : (
                availableStudies.map(study => (
                  <div key={study.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-700">{study.name}</p>
                      <p className="text-xs text-slate-500">{study.sponsor} - {study.questionnaires?.length || 0} questionnaires, {study.visitSchedule?.length || 0} visites</p>
                      {study.inclusionCriteria?.cancerTypes?.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {study.inclusionCriteria.cancerTypes.map(c => (
                            <span key={c} className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded text-[10px]">{c}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleEnroll(study.id)}
                      data-testid={`enroll-study-${study.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 disabled:opacity-50"
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      Enroler
                    </button>
                  </div>
                ))
              )}
              {/* Show already enrolled studies */}
              {enrollments.length > 0 && availableStudies.length > 0 && (
                <div className="pt-3 border-t border-slate-100">
                  <p className="text-xs text-slate-400 mb-2">Deja enrole dans :</p>
                  {enrollments.map(e => (
                    <div key={e.id} className="flex items-center gap-2 p-2 text-xs text-slate-500">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                      {e.studyName}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Responses Modal */}
      {selectedEnrollment && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="font-bold text-slate-800">Scores - {selectedEnrollment.studyName}</h3>
                <p className="text-sm text-slate-500">{responses.length} reponse{responses.length !== 1 ? "s" : ""}</p>
              </div>
              <button onClick={() => { setSelectedEnrollment(null); setResponses([]); }} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {responses.length === 0 ? (
                <div className="text-center py-8">
                  <ClipboardList className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-500 text-sm">Aucune reponse enregistree</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {responses.map(resp => (
                    <div key={resp.id} className="border border-slate-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-700">{resp.questionnaireName}</p>
                          <p className="text-xs text-slate-500">Visite: {resp.visitName} - {resp.completedAt ? new Date(resp.completedAt).toLocaleDateString("fr-FR") : ""}</p>
                        </div>
                        {resp.completionTime && (
                          <span className="text-xs text-slate-400">{Math.round(resp.completionTime / 60)} min</span>
                        )}
                      </div>
                      {resp.computedScores && Object.keys(resp.computedScores).length > 0 && (
                        <div className="grid grid-cols-3 gap-2">
                          {Object.entries(resp.computedScores).filter(([_, v]) => v !== null && typeof v === "number").map(([key, value]) => {
                            const label = key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                            const isFunctional = ["physical functioning", "role functioning", "emotional functioning", "cognitive functioning", "social functioning", "global health status"].includes(key.replace(/_/g, " "));
                            const isGood = isFunctional ? value >= 70 : value <= 30;
                            const isBad = isFunctional ? value < 50 : value > 50;
                            return (
                              <div key={key} className="bg-slate-50 rounded-lg p-2.5">
                                <p className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</p>
                                <p className={`text-lg font-bold ${isGood ? "text-emerald-600" : isBad ? "text-red-600" : "text-amber-600"}`}>
                                  {typeof value === "number" ? value.toFixed(1) : value}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
